import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { auth, googleProvider } from '../firebase';
import {
  signInWithPopup,
  signOut,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  createUserWithEmailAndPassword,
  isSignInWithEmailLink,
  signInWithEmailLink,
  updatePassword,
  updateEmail,
} from 'firebase/auth';
import {
  Alert,
  AlertIcon,
  Button,
  CloseButton,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { getAuthToken } from '../Components/Helpers/script';
import axios from 'axios';

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState();
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState('');
  const [closeBanner, setCloseBanner] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const modalButtonRef = useRef();
  const creditRef = useRef();

  // function setTopLevelError(err) {
  //   setError(err);
  //   setTimeout(() => setError(''), 3000);
  // }

  const setTopLevelError = useCallback(err => {
    setError(err);
    setTimeout(() => setError(''), 3000);
  }, []);

  function signup(email, password) {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  async function signInWithGoogle() {
    return signInWithPopup(auth, googleProvider);
  }

  function logout() {
    return signOut(auth);
  }

  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  function updateEmailHandle(user, email) {
    return updateEmail(user, email);
  }

  function updatePasswordHandle(user, password) {
    return updatePassword(user, password);
  }

  function sendSignInLinkToEmailAuth(email) {
    const origin = window.location.origin;
    const actionCodeSettings = {
      url: origin + '/update-password',
      handleCodeInApp: true,
    };
    return sendSignInLinkToEmail(auth, email, actionCodeSettings);
  }

  function isSignInWithEmailLinkHandle(link) {
    return isSignInWithEmailLink(auth, link);
  }

  async function signInWithEmailLinkHandle(email, link) {
    return signInWithEmailLink(auth, email, link);
  }

  const handleConfirmationModal = async (cb, cost) => {
    // fetch user credits, if freePlan then show modal
    try {
      const token = await getAuthToken(currentUser);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL ?? ''}/api/getUserCredits`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // Handle success response here if necessary

      if (response.status === 200) {
        const { planType } = response.data;
        if (planType === 'freePlan') {
          onOpen();
          modalButtonRef.current = cb;
          creditRef.current = cost;
          return;
        }
      }
    } catch (err) {}
    cb();
  };
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    login,
    signup,
    logout,
    resetPassword,
    updateEmail,
    updatePassword,
    signInWithGoogle,
    sendSignInLinkToEmailAuth,
    isSignInWithEmailLinkHandle,
    signInWithEmailLinkHandle,
    updateEmailHandle,
    updatePasswordHandle,
    setTopLevelError,
    handleConfirmationModal,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && (
        <Flex position="absolute" w="100%" justifyContent="center">
          {error ? (
            <Alert
              status="error"
              display="flex"
              justifyContent="center"
              zIndex={{ base: 1, sm: 0 }}
            >
              <AlertIcon />
              {error}
            </Alert>
          ) : (
            <>
              {!closeBanner && currentUser && (
                <Alert
                  background="linear-gradient(15.46deg, #4A25E1 26.3%, #7B5AFF 86.4%)"
                  display="flex"
                  justifyContent="center"
                  color="white"
                >
                  <Link
                    color="white"
                    href="https://gorgeous-moat-b4e.notion.site/Studywise-PRO-Referral-Program-aa8db23b6a0247a5bc7ec4e69d947e3a"
                    target="_blank"
                    paddingLeft={{ base: 0, xl: '17rem' }}
                    fontStyle="italic"
                  >
                    Claim Studywise PRO for Free.
                  </Link>
                  <CloseButton
                    alignSelf="flex-start"
                    position="fixed"
                    right="5px"
                    top="0.5rem"
                    onClick={() => setCloseBanner(true)}
                  />
                </Alert>
              )}
            </>
          )}
        </Flex>
      )}
      {!loading && children}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Alert</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            You are about to use {creditRef.current ?? 0} credit of your daily
            free credits
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Close
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                modalButtonRef.current();
                onClose();
              }}
            >
              Proceed
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AuthContext.Provider>
  );
}
